/**
 * useCallTheme - Keeps the Chime SDK component library in step with the app theme
 *
 * The Chime components are styled with styled-components and take their colours from a theme
 * object. Every call site used to hand them `lightTheme`, so the call UI stayed light-themed
 * (inside a hard-coded dark shell) in all four app themes. The theme store writes a `dark`
 * class onto <html> (src/app/store/ui.ts), so that class is the single source of truth - the
 * same idiom used by the map (src/components/maps/VaultLocationMap.tsx).
 */

import { useEffect, useState } from 'react';
import { lightTheme, darkTheme } from 'amazon-chime-sdk-component-library-react';

export function useIsDarkTheme() {
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

    useEffect(() => {
        const update = () => setIsDark(document.documentElement.classList.contains('dark'));
        update();
        // Observe the class the theme store toggles so a theme switch mid-call is picked up
        const observer = new MutationObserver(update);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    return isDark;
}

export function useChimeTheme() {
    return useIsDarkTheme() ? darkTheme : lightTheme;
}
